import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { ControlType } from "../../types/controlsRisk";
import useAxiosInstance from "../axiosConfig";

import { Update } from "../../components/Insights";

import { useTenant } from "../../context/tenantContext";
import {
  deleteCollaborator,
  deleteControl,
  deleteHistoryControl,
  deleteRiskTheme,
  deleteTag,
} from "../endpoints/deleteEndPoints";
import {
  getAllCollaborators,
  getAllYears,
  getCategories,
  getControlHistorys,
  getCurrentUserTags,
  getEvolutionChart,
  getInsights,
  getIsAdmin,
  getRiskTheme,
  getRiskThemes,
  getSubcategories,
  getTags,
  getTenants,
} from "../endpoints/getEndPoints";
import {
  addHistoryControl,
  copyRisksBetweenYears,
  createCollaborator,
  CreateCollaboratorDto,
  createTag,
  CreateTagPayload,
  createUpdate,
} from "../endpoints/postEndPoints";
import {
  postCreateRisk,
  putCreateControl,
  updateAdminTags,
  updateControl,
  updateHistoryControl,
  updateRiskTheme,
  updateUpdate,
  updateViewerTags,
} from "../endpoints/updateEndPoints";

export const useRiskThemes = (
  year: number,
  riskPriority?: string[],
  isGlobalGoal?: boolean | null
) => {
  const AxiosInstance = useAxiosInstance();
  const auth = useAuth();
  const { selectedTenant } = useTenant()

  return useQuery({
    queryKey: ["all_risk_themes", year, riskPriority, isGlobalGoal],
    queryFn: () =>
      getRiskThemes(AxiosInstance, year, riskPriority, isGlobalGoal),
    enabled: auth.isAuthenticated && !!selectedTenant,
  });
};

export const useControlHistorys = (
  control_id: number,
  viewHistoryControl: boolean
) => {
  const AxiosInstance = useAxiosInstance();
  return useQuery({
    queryKey: ["all_control_historys", control_id],
    queryFn: () => getControlHistorys(AxiosInstance, control_id),
    enabled: viewHistoryControl,
  });
};

export const useAllYears = () => {
  const AxiosInstance = useAxiosInstance();
  const auth = useAuth();

  return useQuery({
    queryKey: ["all_years"],
    queryFn: () => getAllYears(AxiosInstance),
    enabled: auth.isAuthenticated,
  });
};

export const useRiskTheme = (risk_theme_id: string | undefined) => {
  const AxiosInstance = useAxiosInstance();
  return useQuery({
    queryKey: ["risk_theme", risk_theme_id],
    queryFn: () => getRiskTheme(AxiosInstance, risk_theme_id),
  });
};

export const useCategories = (riskId?: number) => {
  const AxiosInstance = useAxiosInstance();
  return useQuery({
    queryKey: ["all_categories", riskId ?? null],
    queryFn: () => getCategories(AxiosInstance, riskId),
  });
};

export const useSubcategories = (riskId?: number) => {
  const AxiosInstance = useAxiosInstance();
  return useQuery({
    queryKey: ["all_subcategories", riskId ?? null],
    queryFn: () => getSubcategories(AxiosInstance, riskId),
  });
};

export const useAllCollaborators = () => {
  const AxiosInstance = useAxiosInstance();
  return useQuery({
    queryKey: ["all_collaborators"],
    queryFn: () => getAllCollaborators(AxiosInstance),
  });
};

export const useDeleteRiskTheme = () => {
  const AxiosInstance = useAxiosInstance();
  return useMutation({
    mutationFn: (risk_theme_id: number | string | undefined) =>
      deleteRiskTheme(AxiosInstance, risk_theme_id),
  });
};

export const useUpdateRiskTheme = () => {
  const AxiosInstance = useAxiosInstance();
  return useMutation({
    mutationFn: (data: any) => updateRiskTheme(AxiosInstance, data),
  });
};

export const useDeleteControl = () => {
  const AxiosInstance = useAxiosInstance();
  return useMutation({
    mutationFn: (id: number) => deleteControl(AxiosInstance, id),
  });
};

export const useUpdateControl = () => {
  const AxiosInstance = useAxiosInstance();
  return useMutation({
    mutationFn: (data: ControlType) => updateControl(AxiosInstance, data),
  });
};

export const usePostCreateRisk = () => {
  const AxiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => postCreateRisk(AxiosInstance, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all_categories"] });
      queryClient.invalidateQueries({ queryKey: ["all_subcategories"] });
    },
  });
};

export const usePutCreateControl = () => {
  const AxiosInstance = useAxiosInstance();
  return useMutation({
    mutationFn: (data: any) => putCreateControl(AxiosInstance, data),
  });
};

export const useUpdateHistoryControl = () => {
  const AxiosInstance = useAxiosInstance();
  return useMutation({
    mutationFn: (data: any) => updateHistoryControl(AxiosInstance, data),
  });
};

export const useDeleteHistoryControl = () => {
  const AxiosInstance = useAxiosInstance();
  return useMutation({
    mutationFn: (id: number) => deleteHistoryControl(AxiosInstance, id),
  });
};

export const useCreateHistoryControl = () => {
  const AxiosInstance = useAxiosInstance();
  return useMutation({
    mutationFn: (data: any) => addHistoryControl(AxiosInstance, data),
  });
};

export const useGetInsights = (
  riskId: number,
  year: number,
  month?: number | string
) => {
  const AxiosInstance = useAxiosInstance();
  return useQuery({
    queryKey: ["insights", riskId, year, month],
    queryFn: () => getInsights(AxiosInstance, riskId, year, month),
  });
};

export const useGetEvolutionChart = (
  year: number,
  priority: string[],
  okr: string[]
) => {
  const AxiosInstance = useAxiosInstance();
  return useQuery({
    queryKey: ["evolution_chart", year, priority, okr],
    queryFn: () => getEvolutionChart(AxiosInstance, year, priority, okr),
  });
};

export const useGetIsAdmin = (enabled: boolean) => {
  const AxiosInstance = useAxiosInstance();
  return useQuery({
    queryKey: ["is_admin"],
    queryFn: () => getIsAdmin(AxiosInstance),
    enabled,
  });
};

export const useGetTenants = () => {
  const AxiosInstance = useAxiosInstance();
  const auth = useAuth();

  return useQuery({
    queryKey: ["tenants"],
    staleTime: 0,
    queryFn: () => getTenants(AxiosInstance),
    enabled: auth.isAuthenticated,
  });
};

export const useCreateUpdate = (riskId: number) => {
  const axios = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Update) => createUpdate(axios, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["insights", riskId],
      });
    },
  });
};

export const useEditUpdate = (riskId: number) => {
  const axios = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Update) => updateUpdate(axios, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["insights", riskId],
      });
    },
  });
};

export const useCopyRisksBetweenYears = () => {
  const axios = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { sourceYear: number; targetYear: number }) =>
      copyRisksBetweenYears(axios, params),
    onSuccess: () => {
      // Invalidar queries relacionadas a riscos para atualizar a lista
      queryClient.invalidateQueries({ queryKey: ["all_risk_themes"] });
      queryClient.invalidateQueries({ queryKey: ["all_years"] });
    },
  });
};

export const useCreateCollaborator = () => {
  const axios = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCollaboratorDto) =>
      createCollaborator(axios, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all_collaborators"] });
    },
  });
};

export const useDeleteCollaborator = () => {
  const axios = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteCollaborator(axios, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all_collaborators"] });
      queryClient.refetchQueries({ queryKey: ["all_collaborators"] });
    },
  });
};

export const useAllTags = ({ enabled = false }: { enabled?: boolean }) => {
  const axios = useAxiosInstance();
  return useQuery({
    queryKey: ["all_tags"],
    queryFn: () => getTags(axios),
    enabled,
  });
};

export const usePagedTags = ({
  page,
  search,
  pageSize = 10,
}: {
  page: number;
  search: string;
  pageSize?: number;
}) => {
  const axios = useAxiosInstance();
  return useQuery({
    queryKey: ["paged_tags", page, search.trim(), pageSize],
    queryFn: () => getTags(axios, { page, size: pageSize, search }),
  });
};

export const useGetCurrentUserTags = () => {
  const axios = useAxiosInstance();
  return useQuery({
    queryKey: ["current_user_tags"],
    queryFn: () => getCurrentUserTags(axios),
  })
}

export const useCreateTag = () => {
  const axios = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTagPayload) => createTag(axios, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all_tags"] });
      queryClient.invalidateQueries({ queryKey: ["infinite_tags"] });
    },
  });
};

export const useDeleteTag = () => {
  const axios = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId: number) => deleteTag(axios, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all_tags"] });
      queryClient.invalidateQueries({ queryKey: ["infinite_tags"] });
      queryClient.invalidateQueries({ queryKey: ["all_collaborators"] });
    },
  });
};

export const useUpdateViewerTags = () => {
  const axios = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collaboratorId,
      tagIds,
    }: {
      collaboratorId: number;
      tagIds: number[];
    }) => updateViewerTags(axios, collaboratorId, tagIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all_collaborators"] });
    },
  });
};

export const useUpdateAdminTags = () => {
  const axios = useAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collaboratorId,
      tagIds,
    }: {
      collaboratorId: number;
      tagIds: number[];
    }) => updateAdminTags(axios, collaboratorId, tagIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all_collaborators"] });
    },
  });
};
